var _excluded = ["className", "utils", "htmlFor", "required", "text", "action", "isInverse"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import InverseContext from '../../utilities/inverse-context';
import Flex from '../flex';
import Button from '../button/Button';
import Type from '../type';
import StyledLabel from './label.styles';
import StyledLabelOptional from './styles/label.optional.styles';
import StyledLabelAction from './styles/label.action.styles';
import { StyledCacheWrapper } from '../../styles';
var LabelRequiredProps = {
  /**
   * Accepts text: string and onButtonClick: callback function
   */
  action: PropTypes.object,
  /**
   * Add custom CSS classes to component.
   */
  className: PropTypes.string,
  /** Add utility objects for styling */
  utils: PropTypes.object,
  /**
   * Specifies the form field id that the label is attached to
   */
  htmlFor: PropTypes.string,
  /**
   * When false will render (Optional) span to indicate that the form field is not a required field.
   */
  required: PropTypes.bool,
  /**
   * The Label's text.
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** Sets the link with the inverse theme variant */
  isInverse: PropTypes.oneOf(['auto', true, false])
};
var defaultProps = {
  className: '',
  utils: {},
  htmlFor: '',
  required: false,
  text: '',
  action: null,
  isInverse: 'auto'
};

/**
 *
 * @ignore @description
 * ## Label Component
 * The Label is a component that's used for Form elements but in some cases you might need to use it as a stand-alone component.
 *
 * @ignore @see Mesh: [Label Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/informational-label--default)
 *
 * @ignore @example
 * ```jsx
 * import { Label } from 'mesh-component-library';
 *
 * <Label {...props}>
 *  {children}
 * </Label>
 * ```
 * @type React.FC<LabelRequiredProps>
 */

var Label = function Label(_ref) {
  var className = _ref.className,
    utils = _ref.utils,
    htmlFor = _ref.htmlFor,
    required = _ref.required,
    text = _ref.text,
    action = _ref.action,
    isInverse = _ref.isInverse,
    other = _objectWithoutProperties(_ref, _excluded);
  var useIsInverse = React.useContext(InverseContext);
  if (isInverse !== 'auto') {
    useIsInverse = isInverse;
  }
  if (text === '') {
    return null;
  }
  return /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "between",
    wrap: "wrap",
    utils: utils
  }, /*#__PURE__*/React.createElement(StyledLabel, _extends({
    htmlFor: htmlFor,
    className: className
  }, other), /*#__PURE__*/React.createElement(Type, {
    element: "span",
    weight: "bold",
    size: "sm",
    lineHeight: "xs",
    isInverse: useIsInverse
  }, text, !required && /*#__PURE__*/React.createElement(StyledLabelOptional, {
    element: "span",
    size: "xs",
    weight: "semibold",
    variant: "light",
    isInverse: useIsInverse
  }, "(Optional)"))), action && Object.keys(action).length > 0 ? createAction(action, isInverse) : null);
};

/**
 * Helper function to create the label's action.
 *
 * @function createAction
 * @param labelAction action properties
 * @param labelAction.text text of the button action
 * @param labelAction.onButtonClick onClick value for the button
 * @returns {HTMLElement}
 * @private
 */
function createAction(labelAction, isInverse) {
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, null, /*#__PURE__*/React.createElement(StyledLabelAction, null, /*#__PURE__*/React.createElement(Button, {
    variant: "phony",
    isInverse: isInverse,
    onClick: labelAction.onButtonClick
  }, labelAction.text)));
}
Label.propTypes = LabelRequiredProps;
Label.defaultProps = defaultProps;
export default Label;